import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const token = api.getToken();
    if (token) {
      api.getMe()
        .then(user => setCurrentUser(user))
        .catch(() => {
          api.setToken(null);
          setCurrentUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Redirect to VentES OAuth login
  const login = async () => {
    try {
      setError(null);
      const { url } = await api.getLoginUrl();
      window.location.href = url;
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle OAuth callback (exchange code for token)
  const handleCallback = async (code) => {
    try {
      setError(null);
      const result = await api.exchangeCode(code);
      api.setToken(result.access_token);
      setCurrentUser(result.user);
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Logout
  const logout = () => {
    api.setToken(null);
    setCurrentUser(null);
    localStorage.removeItem('enabledApps');
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    handleCallback,
    logout,
    setError,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
